import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ChevronLeft, Target, Trophy, Star, Flame, 
  TrendingUp, Calendar, Clock, Award, Zap,
  BookOpen, Mic, Brain, Users, AlertTriangle,
  CheckCircle, Circle, Lock, BarChart3, Activity,
  CalendarDays, Sparkles, Medal, Crown, GraduationCap,
  Layers, Timer, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module definitions with icons
const MODULE_INFO = {
  'comprehensive': { name: '64 Soruluk Eğitim', icon: BookOpen, color: 'bg-sky-500', milesReward: 80 },
  'flashcards': { name: 'Flashcard', icon: Layers, color: 'bg-amber-500', milesReward: 30 },
  'dragdrop': { name: 'Sürükle & Bırak', icon: Target, color: 'bg-green-500', milesReward: 40 },
  'voicepractice': { name: 'Sesli Pratik', icon: Mic, color: 'bg-pink-500', milesReward: 50 },
  'timedchallenge': { name: 'Zamanlı Yarışma', icon: Timer, color: 'bg-purple-500', milesReward: 60 },
  'roleplay': { name: 'Rol-Play', icon: MessageSquare, color: 'bg-cyan-500', milesReward: 70 },
  'interview': { name: 'Mülakat', icon: Brain, color: 'bg-indigo-500', milesReward: 75 },
  'emergency': { name: 'Acil Durum', icon: AlertTriangle, color: 'bg-red-500', milesReward: 65 }
};

// Level progression
const LEVELS = [
  { name: 'Başlangıç', minMiles: 0, maxMiles: 100, badge: '🌱', title: 'Yeni Başlayan' },
  { name: 'Öğrenci', minMiles: 100, maxMiles: 300, badge: '📚', title: 'Meraklı Öğrenci' },
  { name: 'Aday', minMiles: 300, maxMiles: 600, badge: '✈️', title: 'Umut Vadeden Aday' },
  { name: 'Orta Seviye', minMiles: 600, maxMiles: 1000, badge: '🎯', title: 'Odaklanmış Eğitimci' },
  { name: 'İleri Seviye', minMiles: 1000, maxMiles: 1500, badge: '⭐', title: 'Parlayan Yıldız' },
  { name: 'Uzman', minMiles: 1500, maxMiles: 2500, badge: '🏆', title: 'Kabin Uzmanı' },
  { name: 'Master', minMiles: 2500, maxMiles: 4000, badge: '👑', title: 'Master Eğitmen' },
  { name: 'Profesyonel', minMiles: 4000, maxMiles: Infinity, badge: '💎', title: 'Profesyonel Kabin Memuru' }
];

// Simulated weekly activity data
const generateWeeklyActivity = (totalMiles) => {
  const days = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
  const today = new Date().getDay();
  return days.map((day, index) => ({
    day,
    miles: index <= today ? Math.floor(Math.random() * (totalMiles / 7)) + 10 : 0,
    lessons: index <= today ? Math.floor(Math.random() * 3) : 0,
    isToday: index === (today === 0 ? 6 : today - 1)
  }));
};

// Recent activities simulation
const generateRecentActivities = () => {
  const activities = [
    { type: 'lesson', text: '64 Soruluk Eğitim - A1-A2 tamamlandı', miles: 80, icon: BookOpen, time: '2 saat önce' },
    { type: 'quiz', text: 'Zamanlı Yarışmada %85 başarı', miles: 60, icon: Timer, time: '5 saat önce' },
    { type: 'practice', text: 'Sesli Pratik - 10 cümle', miles: 50, icon: Mic, time: 'Dün' },
    { type: 'interview', text: 'Mülakat Simülasyonu tamamlandı', miles: 75, icon: Brain, time: 'Dün' },
    { type: 'flashcard', text: '30 kelime öğrenildi', miles: 30, icon: Layers, time: '2 gün önce' },
    { type: 'emergency', text: 'Acil Durum Senaryosu tamamlandı', miles: 65, icon: AlertTriangle, time: '3 gün önce' },
  ];
  return activities;
};

// Skill areas with progress
const SKILL_AREAS = [
  { id: 'vocabulary', name: 'Havacılık Terminolojisi', progress: 75, icon: BookOpen, color: 'bg-blue-500' },
  { id: 'icao', name: 'ICAO Alfabesi', progress: 60, icon: GraduationCap, color: 'bg-green-500' },
  { id: 'announcements', name: 'Anonslar', progress: 45, icon: Mic, color: 'bg-pink-500' },
  { id: 'emergency', name: 'Acil Durum Prosedürleri', progress: 55, icon: AlertTriangle, color: 'bg-red-500' },
  { id: 'service', name: 'Yolcu Hizmetleri', progress: 80, icon: Users, color: 'bg-purple-500' },
  { id: 'interview', name: 'Mülakat Becerileri', progress: 40, icon: Brain, color: 'bg-indigo-500' },
];

export default function CabinCrewProgress() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalMiles: 0,
    currentStreak: 0,
    longestStreak: 0,
    lessonsCompleted: 0,
    exercisesCompleted: 0,
    totalTime: 0,
    weeklyMiles: 0,
    monthlyMiles: 0
  });

  const [weeklyActivity, setWeeklyActivity] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cabin-crew/progress`);
      const totalMiles = response.data.total_miles || user?.miles || 890;
      
      setProgress(response.data.progress || {});
      setStats({
        totalMiles: totalMiles,
        currentStreak: response.data.streak || user?.current_streak || 0,
        longestStreak: response.data.longest_streak || user?.longest_streak || 7,
        lessonsCompleted: response.data.completed_lessons || 1,
        exercisesCompleted: Object.keys(response.data.progress || {}).length * 8 || 15,
        totalTime: Math.floor((totalMiles / 10) * 1.5), // Estimated time in minutes
        weeklyMiles: Math.floor(totalMiles * 0.3),
        monthlyMiles: Math.floor(totalMiles * 0.7)
      });
      
      setWeeklyActivity(generateWeeklyActivity(totalMiles));
      setRecentActivities(generateRecentActivities());
    } catch (error) {
      console.error('Progress fetch error:', error);
      const fallbackMiles = user?.miles || 890;
      setStats({
        totalMiles: fallbackMiles,
        currentStreak: user?.current_streak || 0,
        longestStreak: user?.longest_streak || 7,
        lessonsCompleted: 1,
        exercisesCompleted: 15,
        totalTime: 90,
        weeklyMiles: Math.floor(fallbackMiles * 0.3),
        monthlyMiles: Math.floor(fallbackMiles * 0.7)
      });
      setWeeklyActivity(generateWeeklyActivity(fallbackMiles));
      setRecentActivities(generateRecentActivities());
    } finally {
      setLoading(false);
    }
  };

  // Calculate current level
  const getCurrentLevel = (xp) => {
    for (let i = LEVELS.length - 1; i >= 0; i--) {
      if (xp >= LEVELS[i].minMiles) {
        return { ...LEVELS[i], index: i };
      }
    }
    return { ...LEVELS[0], index: 0 };
  };

  const currentLevel = getCurrentLevel(stats.totalMiles);
  const nextLevel = LEVELS[currentLevel.index + 1] || currentLevel;
  const levelProgress = currentLevel.maxMiles === Infinity 
    ? 100 
    : ((stats.totalMiles - currentLevel.minMiles) / (currentLevel.maxMiles - currentLevel.minMiles)) * 100;

  // Module completion status
  const getModuleStatus = (moduleId) => {
    if (!progress) return 'not_started';
    const moduleProgress = progress[moduleId];
    if (!moduleProgress) return 'not_started';
    if (moduleProgress.completed) return 'completed';
    return 'in_progress';
  };

  // Calculate max weekly XP for chart scaling
  const maxWeeklyMiles = Math.max(...weeklyActivity.map(d => d.miles), 50);

  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">İlerleme yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-800 flex items-center gap-3">
              <Target className="w-8 h-8 text-green-500" />
              İlerleme Durumum
            </h1>
            <p className="text-gray-600">Kabin memurluğu eğitimindeki gelişiminizi takip edin</p>
          </div>

          {/* Level Progress Card - Enhanced */}
          <Card className="mb-8 border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-200/30 rounded-full -mr-10 -mt-10"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-emerald-200/30 rounded-full -ml-8 -mb-8"></div>
            <CardContent className="p-6 relative">
              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* Level Badge */}
                <div className="text-center">
                  <div className="w-28 h-28 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-xl mb-2 ring-4 ring-green-200">
                    <span className="text-6xl">{currentLevel.badge}</span>
                  </div>
                  <p className="font-black text-xl text-gray-800">{currentLevel.name}</p>
                  <p className="text-sm text-gray-500">{currentLevel.title}</p>
                  <Badge className="mt-2 bg-green-500">Seviye {currentLevel.index + 1}</Badge>
                </div>

                {/* Progress */}
                <div className="flex-1 w-full">
                  <div className="flex justify-between mb-2">
                    <span className="text-lg font-bold text-green-600">
                      {stats.totalMiles} Mil
                    </span>
                    <span className="text-sm font-medium text-gray-500">
                      {currentLevel.maxMiles === Infinity ? 'MAX' : `${currentLevel.maxMiles} Mil`}
                    </span>
                  </div>
                  <div className="relative">
                    <Progress value={levelProgress} className="h-6 mb-2" />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white drop-shadow">
                      {Math.round(levelProgress)}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 text-center mt-2">
                    {currentLevel.maxMiles === Infinity 
                      ? '🎉 Maximum seviyeye ulaştınız! Tebrikler!'
                      : `Sonraki seviye için ${currentLevel.maxMiles - stats.totalMiles} Mil daha kazanın`
                    }
                  </p>

                  {/* Next Level Preview */}
                  {currentLevel.index < LEVELS.length - 1 && (
                    <div className="mt-4 flex items-center justify-center gap-3 p-3 bg-white/50 rounded-xl">
                      <span className="text-3xl">{nextLevel.badge}</span>
                      <div>
                        <p className="text-sm text-gray-600">Sıradaki Seviye:</p>
                        <p className="font-bold text-gray-800">{nextLevel.name}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs for different views */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Genel Bakış</span>
              </TabsTrigger>
              <TabsTrigger value="modules" className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                <span className="hidden sm:inline">Modüller</span>
              </TabsTrigger>
              <TabsTrigger value="skills" className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                <span className="hidden sm:inline">Beceriler</span>
              </TabsTrigger>
              <TabsTrigger value="roadmap" className="flex items-center gap-2">
                <Award className="w-4 h-4" />
                <span className="hidden sm:inline">Yol Haritası</span>
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50">
                  <CardContent className="p-4 text-center">
                    <Star className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-3xl font-black text-amber-600">{stats.totalMiles}</p>
                    <p className="text-sm text-gray-600">Toplam Mil</p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-red-50">
                  <CardContent className="p-4 text-center">
                    <Flame className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                    <p className="text-3xl font-black text-orange-600">{stats.currentStreak}</p>
                    <p className="text-sm text-gray-600">Günlük Seri</p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-3xl font-black text-green-600">{stats.lessonsCompleted}</p>
                    <p className="text-sm text-gray-600">Tamamlanan Ders</p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                    <p className="text-3xl font-black text-purple-600">{stats.totalTime}</p>
                    <p className="text-sm text-gray-600">Dakika Çalışma</p>
                  </CardContent>
                </Card>
              </div>

              {/* Weekly Activity Chart */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-blue-500" />
                    Haftalık Aktivite
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between gap-2 h-40">
                    {weeklyActivity.map((day, index) => (
                      <div key={index} className="flex-1 flex flex-col items-center">
                        <div className="w-full flex flex-col items-center justify-end" style={{ height: '120px' }}>
                          <div 
                            className={`w-full max-w-[40px] rounded-t-lg transition-all ${
                              day.isToday 
                                ? 'bg-gradient-to-t from-green-500 to-emerald-400' 
                                : day.miles > 0 
                                  ? 'bg-gradient-to-t from-blue-400 to-cyan-300' 
                                  : 'bg-gray-200'
                            }`}
                            style={{ height: `${(day.miles / maxWeeklyMiles) * 100}%`, minHeight: day.miles > 0 ? '20px' : '4px' }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{day.day}</p>
                        <p className={`text-xs font-bold ${day.isToday ? 'text-green-600' : 'text-gray-600'}`}>
                          {day.miles > 0 ? `${day.miles}` : '-'}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-4 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{stats.weeklyMiles}</p>
                      <p className="text-xs text-gray-500">Bu Hafta Mil</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">{stats.monthlyMiles}</p>
                      <p className="text-xs text-gray-500">Bu Ay Mil</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{stats.longestStreak}</p>
                      <p className="text-xs text-gray-500">En Uzun Seri</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activities */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-green-500" />
                    Son Aktiviteler
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {recentActivities.map((activity, index) => {
                      const Icon = activity.icon;
                      return (
                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Icon className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{activity.text}</p>
                            <p className="text-xs text-gray-500">{activity.time}</p>
                          </div>
                          <Badge className="bg-green-100 text-green-700">+{activity.xp} Mil</Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Modules Tab */}
            <TabsContent value="modules">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-green-500" />
                    Modül İlerlemesi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(MODULE_INFO).map(([moduleId, info]) => {
                      const status = getModuleStatus(moduleId);
                      const Icon = info.icon;
                      
                      return (
                        <div
                          key={moduleId}
                          className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                            status === 'completed' ? 'border-green-300 bg-green-50' :
                            status === 'in_progress' ? 'border-amber-300 bg-amber-50' :
                            'border-gray-200 bg-gray-50 hover:border-blue-300'
                          }`}
                          onClick={() => navigate(`/cabin-crew/${moduleId === 'comprehensive' ? 'modules' : moduleId.replace('challenge', '-challenge')}`)}
                        >
                          <div className={`w-14 h-14 ${info.color} rounded-xl flex items-center justify-center shadow-md`}>
                            <Icon className="w-7 h-7 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-800">{info.name}</h4>
                            <p className="text-sm text-gray-500">
                              {status === 'completed' ? '✓ Tamamlandı' :
                               status === 'in_progress' ? '⏳ Devam ediyor...' :
                               'Başlamak için tıklayın'}
                            </p>
                            <Badge variant="outline" className="mt-1 text-xs">
                              +{info.milesReward} Mil
                            </Badge>
                          </div>
                          <div>
                            {status === 'completed' ? (
                              <CheckCircle className="w-8 h-8 text-green-500" />
                            ) : status === 'in_progress' ? (
                              <div className="w-8 h-8 border-4 border-amber-300 border-t-amber-500 rounded-full animate-spin"></div>
                            ) : (
                              <Circle className="w-8 h-8 text-gray-300" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Skills Tab */}
            <TabsContent value="skills">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    Beceri Alanları
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {SKILL_AREAS.map((skill) => {
                      const Icon = skill.icon;
                      return (
                        <div key={skill.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 ${skill.color} rounded-lg flex items-center justify-center`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <span className="font-medium text-gray-800">{skill.name}</span>
                            </div>
                            <span className="font-bold text-gray-600">{skill.progress}%</span>
                          </div>
                          <div className="relative">
                            <Progress value={skill.progress} className="h-3" />
                          </div>
                          <p className="text-xs text-gray-500">
                            {skill.progress >= 80 ? '🌟 Mükemmel! Bu alanda çok iyisiniz.' :
                             skill.progress >= 60 ? '👍 İyi gidiyorsunuz, biraz daha pratik yapın.' :
                             skill.progress >= 40 ? '💪 Gelişmeye devam edin.' :
                             '📚 Bu alana daha fazla zaman ayırın.'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Roadmap Tab */}
            <TabsContent value="roadmap">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-500" />
                    Seviye Yol Haritası
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {LEVELS.map((level, index) => {
                      const isCurrentLevel = index === currentLevel.index;
                      const isCompleted = stats.totalMiles >= level.maxMiles;
                      const isLocked = stats.totalMiles < level.minMiles;
                      
                      return (
                        <div
                          key={index}
                          className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                            isCurrentLevel ? 'bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-400 shadow-lg' :
                            isCompleted ? 'bg-gray-50 border border-gray-200' :
                            'bg-gray-50 border border-gray-200 opacity-60'
                          }`}
                        >
                          <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-md ${
                            isCurrentLevel ? 'bg-gradient-to-br from-green-400 to-emerald-500 ring-4 ring-green-200' :
                            isCompleted ? 'bg-gradient-to-br from-gray-400 to-gray-500' :
                            'bg-gray-300'
                          }`}>
                            <span className="text-3xl">{level.badge}</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-bold text-gray-800 text-lg">{level.name}</h4>
                              {isCurrentLevel && (
                                <Badge className="bg-green-500 text-white animate-pulse">Şu anki seviye</Badge>
                              )}
                              {isCompleted && !isCurrentLevel && (
                                <Badge className="bg-gray-500 text-white">✓ Tamamlandı</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{level.title}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {level.minMiles} - {level.maxMiles === Infinity ? '∞' : level.maxMiles} Mil
                            </p>
                          </div>
                          <div>
                            {isCompleted ? (
                              <Medal className="w-8 h-8 text-amber-500" />
                            ) : isCurrentLevel ? (
                              <TrendingUp className="w-8 h-8 text-green-500 animate-bounce" />
                            ) : (
                              <Lock className="w-8 h-8 text-gray-400" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Motivational Footer */}
          <Card className="mt-8 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
            <CardContent className="p-6 text-center">
              <Sparkles className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Harika Gidiyorsunuz! 🚀</h3>
              <p className="text-white/80">
                Şu ana kadar {stats.totalMiles} Mil kazandınız ve {stats.lessonsCompleted} ders tamamladınız.
                Her gün pratik yaparak kabin memurluğu hedefinize yaklaşın!
              </p>
              <Button 
                variant="secondary" 
                className="mt-4"
                onClick={() => navigate('/cabin-crew/modules')}
              >
                Eğitime Devam Et
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </CabinCrewLayout>
  );
}
